import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import {
  Calendar,
  Save,
  Clock,
  DollarSign,
  AlertCircle,
  CheckCircle,
  Plus,
  Trash2,
  Settings as SettingsIcon,
} from "lucide-react";
import { useTheme } from "../../context/ThemeContext";
import useCompany from "../../api/company";

const DAYS_OF_WEEK = [
  { key: "monday", label: "Maandag" },
  { key: "tuesday", label: "Dinsdag" },
  { key: "wednesday", label: "Woensdag" },
  { key: "thursday", label: "Donderdag" },
  { key: "friday", label: "Vrijdag" },
  { key: "saturday", label: "Zaterdag" },
  { key: "sunday", label: "Zondag" },
];

const Settings = () => {
  const { getCompanyById, updateCompany } = useCompany();
  const { theme } = useTheme();
  const isDark = theme === "dark";
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [error, setError] = useState(null);

  const [reservationSettings, setReservationSettings] = useState({
    enabled: true,
    workingHours: {
      monday: { enabled: true, start: "09:00", end: "17:00" },
      tuesday: { enabled: true, start: "09:00", end: "17:00" },
      wednesday: { enabled: true, start: "09:00", end: "17:00" },
      thursday: { enabled: true, start: "09:00", end: "17:00" },
      friday: { enabled: true, start: "09:00", end: "17:00" },
      saturday: { enabled: false, start: "09:00", end: "17:00" },
      sunday: { enabled: false, start: "09:00", end: "17:00" },
    },
    services: [],
    slotDuration: 30,
    autoConfirm: false,
    minAdvanceBooking: 24,
    maxAdvanceBooking: 90,
    bufferTime: 0,
  });

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const companyId = localStorage.getItem("companyId");
      const companyData = await getCompanyById(companyId);

      if (companyData.reservation_settings) {
        setReservationSettings({
          ...reservationSettings,
          ...companyData.reservation_settings,
        });
      }
    } catch (err) {
      console.error("Error fetching settings:", err);
      setError("Fout bij laden instellingen");
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);
      setSaveSuccess(false);

      const companyId = localStorage.getItem("companyId");
      await updateCompany(companyId, {
        reservation_settings: reservationSettings,
      });

      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (err) {
      console.error("Error saving settings:", err);
      setError("Fout bij opslaan instellingen");
    } finally {
      setSaving(false);
    }
  };

  const handleWorkingHoursChange = (day, field, value) => {
    setReservationSettings({
      ...reservationSettings,
      workingHours: {
        ...reservationSettings.workingHours,
        [day]: {
          ...reservationSettings.workingHours[day],
          [field]: value,
        },
      },
    });
  };

  const addService = () => {
    setReservationSettings({
      ...reservationSettings,
      services: [
        ...reservationSettings.services,
        { name: "", duration: 30, price: 0 },
      ],
    });
  };

  const removeService = (index) => {
    setReservationSettings({
      ...reservationSettings,
      services: reservationSettings.services.filter((_, i) => i !== index),
    });
  };

  const updateService = (index, field, value) => {
    const newServices = [...reservationSettings.services];
    newServices[index] = { ...newServices[index], [field]: value };
    setReservationSettings({
      ...reservationSettings,
      services: newServices,
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div
          className={`animate-spin rounded-full h-6 w-6 border-2 ${isDark ? "border-white/20 border-t-white" : "border-gray-200 border-t-gray-900"}`}
        ></div>
      </div>
    );
  }

  return (
    <div
      className={`h-full overflow-y-auto p-4 md:p-6 ${isDark ? "bg-[#0a0a0a]" : "bg-[#f8f8fa]"}`}
    >
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="max-w-5xl mx-auto space-y-4"
      >
        {/* Header */}
        <div className="mb-2">
          <h1
            className={`text-[15px] font-semibold ${isDark ? "text-white" : "text-gray-900"}`}
          >
            Reservatie Instellingen
          </h1>
          <p
            className={`text-[12px] mt-0.5 ${isDark ? "text-gray-500" : "text-gray-400"}`}
          >
            Configureer openingstijden, diensten en boekingsregels
          </p>
        </div>

        {/* Success/Error Messages */}
        {saveSuccess && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className={`p-3 rounded-xl flex items-center gap-2 text-[13px] ${isDark ? "bg-green-500/10 text-green-400 border border-green-500/20" : "bg-green-50 text-green-700 border border-green-200"}`}
          >
            <CheckCircle className="w-4 h-4 flex-shrink-0" />
            <span>Instellingen succesvol opgeslagen!</span>
          </motion.div>
        )}

        {error && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className={`p-3 rounded-xl flex items-center gap-2 text-[13px] ${isDark ? "bg-red-500/10 text-red-400 border border-red-500/20" : "bg-red-50 text-red-700 border border-red-200"}`}
          >
            <AlertCircle className="w-4 h-4 flex-shrink-0" />
            <span>{error}</span>
          </motion.div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Working Hours Card */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className={`rounded-xl border overflow-hidden ${isDark ? "bg-white/[0.03] border-white/[0.06]" : "bg-white border-gray-200/80"}`}
          >
            <div
              className={`px-5 py-4 border-b ${isDark ? "border-white/[0.06]" : "border-gray-100"}`}
            >
              <div className="flex items-center gap-3">
                <div
                  className={`h-8 w-8 rounded-lg flex items-center justify-center ${isDark ? "bg-white/[0.06]" : "bg-gray-100"}`}
                >
                  <Calendar
                    className={`w-4 h-4 ${isDark ? "text-gray-400" : "text-gray-500"}`}
                  />
                </div>
                <div>
                  <h2
                    className={`text-[13px] font-semibold ${isDark ? "text-white" : "text-gray-900"}`}
                  >
                    Openingstijden
                  </h2>
                  <p
                    className={`text-[11px] ${isDark ? "text-gray-500" : "text-gray-400"}`}
                  >
                    Stel je werkuren per dag in
                  </p>
                </div>
              </div>
            </div>

            <div className="p-4 space-y-2 max-h-[400px] overflow-y-auto">
              {DAYS_OF_WEEK.map((day) => (
                <div
                  key={day.key}
                  className={`flex items-center gap-3 p-2.5 rounded-lg transition-colors ${
                    reservationSettings.workingHours[day.key].enabled
                      ? isDark
                        ? "bg-white/[0.02]"
                        : "bg-gray-50/50"
                      : "opacity-50"
                  }`}
                >
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={
                        reservationSettings.workingHours[day.key].enabled
                      }
                      onChange={(e) =>
                        handleWorkingHoursChange(
                          day.key,
                          "enabled",
                          e.target.checked,
                        )
                      }
                      className="sr-only peer"
                    />
                    <div
                      className={`w-8 h-[18px] rounded-full peer peer-checked:after:translate-x-[14px] after:content-[''] after:absolute after:top-[3px] after:left-[3px] after:bg-white after:rounded-full after:h-3 after:w-3 after:transition-all ${
                        reservationSettings.workingHours[day.key].enabled
                          ? isDark
                            ? "bg-white"
                            : "bg-gray-900"
                          : isDark
                            ? "bg-white/10"
                            : "bg-gray-300"
                      }`}
                    >
                      {reservationSettings.workingHours[day.key].enabled && (
                        <div
                          className={`absolute top-[3px] left-[3px] w-3 h-3 rounded-full translate-x-[14px] ${isDark ? "bg-black" : "bg-white"}`}
                        ></div>
                      )}
                      {!reservationSettings.workingHours[day.key].enabled && (
                        <div className="absolute top-[3px] left-[3px] w-3 h-3 rounded-full bg-white"></div>
                      )}
                    </div>
                  </label>
                  <span
                    className={`w-20 text-[12px] font-medium ${isDark ? "text-gray-300" : "text-gray-700"}`}
                  >
                    {day.label}
                  </span>
                  <div className="flex items-center gap-2 flex-1">
                    <input
                      type="time"
                      value={reservationSettings.workingHours[day.key].start}
                      onChange={(e) =>
                        handleWorkingHoursChange(
                          day.key,
                          "start",
                          e.target.value,
                        )
                      }
                      disabled={
                        !reservationSettings.workingHours[day.key].enabled
                      }
                      className={`flex-1 h-8 px-2 rounded-lg text-[12px] border ${isDark ? "bg-white/[0.04] border-white/[0.06] text-white disabled:opacity-30" : "bg-gray-50 border-gray-200 text-gray-900 disabled:bg-gray-100 disabled:text-gray-300"}`}
                    />
                    <span
                      className={`text-[11px] ${isDark ? "text-gray-600" : "text-gray-400"}`}
                    >
                      tot
                    </span>
                    <input
                      type="time"
                      value={reservationSettings.workingHours[day.key].end}
                      onChange={(e) =>
                        handleWorkingHoursChange(day.key, "end", e.target.value)
                      }
                      disabled={
                        !reservationSettings.workingHours[day.key].enabled
                      }
                      className={`flex-1 h-8 px-2 rounded-lg text-[12px] border ${isDark ? "bg-white/[0.04] border-white/[0.06] text-white disabled:opacity-30" : "bg-gray-50 border-gray-200 text-gray-900 disabled:bg-gray-100 disabled:text-gray-300"}`}
                    />
                  </div>
                </div>
              ))}
            </div>
          </motion.div>

          {/* Services Card */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className={`rounded-xl border overflow-hidden ${isDark ? "bg-white/[0.03] border-white/[0.06]" : "bg-white border-gray-200/80"}`}
          >
            <div
              className={`px-5 py-4 border-b flex items-center justify-between ${isDark ? "border-white/[0.06]" : "border-gray-100"}`}
            >
              <div className="flex items-center gap-3">
                <div
                  className={`h-8 w-8 rounded-lg flex items-center justify-center ${isDark ? "bg-white/[0.06]" : "bg-gray-100"}`}
                >
                  <SettingsIcon
                    className={`w-4 h-4 ${isDark ? "text-gray-400" : "text-gray-500"}`}
                  />
                </div>
                <div>
                  <h2
                    className={`text-[13px] font-semibold ${isDark ? "text-white" : "text-gray-900"}`}
                  >
                    Diensten
                  </h2>
                  <p
                    className={`text-[11px] ${isDark ? "text-gray-500" : "text-gray-400"}`}
                  >
                    Beheer je beschikbare diensten
                  </p>
                </div>
              </div>
              <button
                onClick={addService}
                className={`h-8 px-3 rounded-lg text-[12px] font-medium flex items-center gap-1.5 transition-colors ${isDark ? "bg-white text-black hover:bg-gray-200" : "bg-gray-900 text-white hover:bg-gray-800"}`}
              >
                <Plus className="w-3.5 h-3.5" />
                Toevoegen
              </button>
            </div>

            <div className="p-4 space-y-3 max-h-[400px] overflow-y-auto">
              {reservationSettings.services.length === 0 ? (
                <div className="text-center py-8">
                  <div
                    className={`h-10 w-10 rounded-xl mx-auto mb-2 flex items-center justify-center ${isDark ? "bg-white/[0.04]" : "bg-gray-50"}`}
                  >
                    <SettingsIcon
                      className={`h-5 w-5 ${isDark ? "text-gray-600" : "text-gray-300"}`}
                    />
                  </div>
                  <p
                    className={`text-[12px] ${isDark ? "text-gray-500" : "text-gray-400"}`}
                  >
                    Nog geen diensten toegevoegd
                  </p>
                </div>
              ) : (
                reservationSettings.services.map((service, index) => (
                  <div
                    key={index}
                    className={`p-3 rounded-xl border space-y-3 ${isDark ? "bg-white/[0.02] border-white/[0.06]" : "bg-gray-50/50 border-gray-100"}`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <input
                        type="text"
                        value={service.name}
                        onChange={(e) =>
                          updateService(index, "name", e.target.value)
                        }
                        placeholder="Dienst naam"
                        className={`flex-1 h-8 px-3 rounded-lg text-[13px] border ${isDark ? "bg-white/[0.04] border-white/[0.06] text-white placeholder-gray-600" : "bg-white border-gray-200 text-gray-900 placeholder-gray-400"}`}
                      />
                      <button
                        onClick={() => removeService(index)}
                        className={`h-8 w-8 rounded-lg flex items-center justify-center transition-colors ${isDark ? "text-red-400 hover:bg-red-500/10" : "text-red-500 hover:bg-red-50"}`}
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <label
                          className={`flex items-center gap-1 text-[11px] font-medium mb-1 ${isDark ? "text-gray-500" : "text-gray-400"}`}
                        >
                          <Clock className="w-3 h-3" />
                          Duur (min)
                        </label>
                        <input
                          type="number"
                          value={service.duration}
                          onChange={(e) =>
                            updateService(
                              index,
                              "duration",
                              parseInt(e.target.value) || 0,
                            )
                          }
                          min="5"
                          step="5"
                          className={`w-full h-8 px-3 rounded-lg text-[12px] border ${isDark ? "bg-white/[0.04] border-white/[0.06] text-white" : "bg-white border-gray-200 text-gray-900"}`}
                        />
                      </div>
                      <div>
                        <label
                          className={`flex items-center gap-1 text-[11px] font-medium mb-1 ${isDark ? "text-gray-500" : "text-gray-400"}`}
                        >
                          <DollarSign className="w-3 h-3" />
                          Prijs (€)
                        </label>
                        <input
                          type="number"
                          value={service.price}
                          onChange={(e) =>
                            updateService(
                              index,
                              "price",
                              parseFloat(e.target.value) || 0,
                            )
                          }
                          min="0"
                          step="0.01"
                          className={`w-full h-8 px-3 rounded-lg text-[12px] border ${isDark ? "bg-white/[0.04] border-white/[0.06] text-white" : "bg-white border-gray-200 text-gray-900"}`}
                        />
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </motion.div>
        </div>

        {/* Booking Configuration Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className={`rounded-xl border overflow-hidden ${isDark ? "bg-white/[0.03] border-white/[0.06]" : "bg-white border-gray-200/80"}`}
        >
          <div
            className={`px-5 py-4 border-b ${isDark ? "border-white/[0.06]" : "border-gray-100"}`}
          >
            <div className="flex items-center gap-3">
              <div
                className={`h-8 w-8 rounded-lg flex items-center justify-center ${isDark ? "bg-white/[0.06]" : "bg-gray-100"}`}
              >
                <Clock
                  className={`w-4 h-4 ${isDark ? "text-gray-400" : "text-gray-500"}`}
                />
              </div>
              <div>
                <h2
                  className={`text-[13px] font-semibold ${isDark ? "text-white" : "text-gray-900"}`}
                >
                  Boekingsconfiguratie
                </h2>
                <p
                  className={`text-[11px] ${isDark ? "text-gray-500" : "text-gray-400"}`}
                >
                  Algemene instellingen voor reserveringen
                </p>
              </div>
            </div>
          </div>

          <div className="p-5 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <div>
              <label
                className={`block text-[11px] font-medium mb-1.5 ${isDark ? "text-gray-400" : "text-gray-500"}`}
              >
                Tijdslot duur (minuten)
              </label>
              <select
                value={reservationSettings.slotDuration}
                onChange={(e) =>
                  setReservationSettings({
                    ...reservationSettings,
                    slotDuration: parseInt(e.target.value),
                  })
                }
                className={`w-full h-9 px-3 rounded-lg text-[13px] border ${isDark ? "bg-white/[0.04] border-white/[0.06] text-white" : "bg-gray-50 border-gray-200 text-gray-900"}`}
              >
                <option value="15">15 minuten</option>
                <option value="30">30 minuten</option>
                <option value="45">45 minuten</option>
                <option value="60">60 minuten</option>
              </select>
            </div>

            <div>
              <label
                className={`block text-[11px] font-medium mb-1.5 ${isDark ? "text-gray-400" : "text-gray-500"}`}
              >
                Min. vooraf boeken (uur)
              </label>
              <input
                type="number"
                value={reservationSettings.minAdvanceBooking}
                onChange={(e) =>
                  setReservationSettings({
                    ...reservationSettings,
                    minAdvanceBooking: parseInt(e.target.value) || 0,
                  })
                }
                min="0"
                className={`w-full h-9 px-3 rounded-lg text-[13px] border ${isDark ? "bg-white/[0.04] border-white/[0.06] text-white" : "bg-gray-50 border-gray-200 text-gray-900"}`}
              />
            </div>

            <div>
              <label
                className={`block text-[11px] font-medium mb-1.5 ${isDark ? "text-gray-400" : "text-gray-500"}`}
              >
                Max. vooraf boeken (dagen)
              </label>
              <input
                type="number"
                value={reservationSettings.maxAdvanceBooking}
                onChange={(e) =>
                  setReservationSettings({
                    ...reservationSettings,
                    maxAdvanceBooking: parseInt(e.target.value) || 0,
                  })
                }
                min="1"
                className={`w-full h-9 px-3 rounded-lg text-[13px] border ${isDark ? "bg-white/[0.04] border-white/[0.06] text-white" : "bg-gray-50 border-gray-200 text-gray-900"}`}
              />
            </div>

            <div>
              <label
                className={`block text-[11px] font-medium mb-1.5 ${isDark ? "text-gray-400" : "text-gray-500"}`}
              >
                Buffer tijd (minuten)
              </label>
              <input
                type="number"
                value={reservationSettings.bufferTime}
                onChange={(e) =>
                  setReservationSettings({
                    ...reservationSettings,
                    bufferTime: parseInt(e.target.value) || 0,
                  })
                }
                min="0"
                step="5"
                className={`w-full h-9 px-3 rounded-lg text-[13px] border ${isDark ? "bg-white/[0.04] border-white/[0.06] text-white" : "bg-gray-50 border-gray-200 text-gray-900"}`}
              />
            </div>

            <div
              className={`flex items-center gap-3 p-3 rounded-xl ${isDark ? "bg-white/[0.02]" : "bg-gray-50/50"}`}
            >
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={reservationSettings.autoConfirm}
                  onChange={(e) =>
                    setReservationSettings({
                      ...reservationSettings,
                      autoConfirm: e.target.checked,
                    })
                  }
                  className="sr-only peer"
                />
                <div
                  className={`w-8 h-[18px] rounded-full relative ${
                    reservationSettings.autoConfirm
                      ? isDark
                        ? "bg-white"
                        : "bg-gray-900"
                      : isDark
                        ? "bg-white/10"
                        : "bg-gray-300"
                  }`}
                >
                  <div
                    className={`absolute top-[3px] w-3 h-3 rounded-full transition-all ${
                      reservationSettings.autoConfirm
                        ? `left-[17px] ${isDark ? "bg-black" : "bg-white"}`
                        : "left-[3px] bg-white"
                    }`}
                  ></div>
                </div>
              </label>
              <label
                className={`text-[12px] font-medium cursor-pointer ${isDark ? "text-gray-300" : "text-gray-700"}`}
              >
                Automatisch bevestigen
              </label>
            </div>

            <div
              className={`flex items-center gap-3 p-3 rounded-xl ${isDark ? "bg-white/[0.02]" : "bg-gray-50/50"}`}
            >
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={reservationSettings.enabled}
                  onChange={(e) =>
                    setReservationSettings({
                      ...reservationSettings,
                      enabled: e.target.checked,
                    })
                  }
                  className="sr-only peer"
                />
                <div
                  className={`w-8 h-[18px] rounded-full relative ${
                    reservationSettings.enabled
                      ? isDark
                        ? "bg-white"
                        : "bg-gray-900"
                      : isDark
                        ? "bg-white/10"
                        : "bg-gray-300"
                  }`}
                >
                  <div
                    className={`absolute top-[3px] w-3 h-3 rounded-full transition-all ${
                      reservationSettings.enabled
                        ? `left-[17px] ${isDark ? "bg-black" : "bg-white"}`
                        : "left-[3px] bg-white"
                    }`}
                  ></div>
                </div>
              </label>
              <label
                className={`text-[12px] font-medium cursor-pointer ${isDark ? "text-gray-300" : "text-gray-700"}`}
              >
                Reserveringen ingeschakeld
              </label>
            </div>
          </div>
        </motion.div>

        {/* Save Button */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
        >
          <button
            onClick={handleSave}
            disabled={saving}
            className={`w-full h-10 rounded-xl text-[13px] font-medium flex items-center justify-center gap-2 transition-colors disabled:opacity-50 ${isDark ? "bg-white text-black hover:bg-gray-200" : "bg-gray-900 text-white hover:bg-gray-800"}`}
          >
            <Save className="w-4 h-4" />
            {saving ? "Opslaan..." : "Alle Instellingen Opslaan"}
          </button>
        </motion.div>
      </motion.div>
    </div>
  );
};

export default Settings;
